/**
 * Integration utilities for working with push integrations and action subtypes
 */
import { ALL_INTEGRATIONS, } from '../constants/integrations.js';
/**
 * Get action subtypes for an integration
 * Returns null if integration doesn't have action subtypes
 */
export function getIntegrationActionSubtypes(integrationType) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    return (integration
        ?.ACTION_SUBTYPES || null);
}
/**
 * Get display name for a push integration option
 * e.g., "AppFolio - Service Request" or "Buildium"
 */
export function getPushIntegrationDisplayName(integrationType, actionSubtype) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    if (!integration)
        return 'Unknown Integration';
    const baseName = integration.NAME;
    const actionSubtypes = integration?.ACTION_SUBTYPES;
    if (!actionSubtypes || !actionSubtype) {
        return baseName;
    }
    const subtypeConfig = actionSubtypes.find(st => st.type === actionSubtype);
    return subtypeConfig ? `${baseName} - ${subtypeConfig.name}` : baseName;
}
/**
 * Get event type for a push integration action
 * Used by handoff event handler to determine which cloud function to trigger
 */
export function getPushIntegrationEventType(integrationType, actionSubtype) {
    const integration = ALL_INTEGRATIONS.find(i => i.TYPE === integrationType);
    if (!integration)
        return null;
    const actionSubtypes = integration?.ACTION_SUBTYPES;
    if (!actionSubtypes) {
        // Integration without action subtypes - not a push-enabled integration
        return null;
    }
    if (!actionSubtype) {
        // Legacy data with null actionSubtype - fallback to first subtype's event
        // This ensures existing workflows continue to work after adding subtypes
        return actionSubtypes[0]?.eventType || null;
    }
    const subtypeConfig = actionSubtypes.find(st => st.type === actionSubtype);
    return subtypeConfig?.eventType || null;
}
/**
 * Get variants for a specific action subtype within an integration
 * Returns null if the subtype doesn't exist or has no variants
 */
export function getActionSubtypeVariants(integrationType, actionSubtype) {
    const subtypes = getIntegrationActionSubtypes(integrationType);
    if (!subtypes)
        return null;
    const config = subtypes.find(s => s.type === actionSubtype);
    return config?.variants || null;
}
/**
 * Get handoff-level field definitions for a given integration subtype/variant.
 * Returns the fields that can be configured per-handoff to override global integration defaults.
 */
export function getHandoffFields(integrationType, actionSubtype, variantId) {
    const subtypes = getIntegrationActionSubtypes(integrationType);
    if (!subtypes)
        return [];
    const config = subtypes.find(s => s.type === actionSubtype);
    if (!config)
        return [];
    // If variant is specified, look up fields from the variant
    if (variantId && config.variants) {
        const variant = config.variants.find(v => v.id === variantId);
        return variant?.handoffFields || [];
    }
    // Otherwise use subtype-level fields
    return config.handoffFields || [];
}
/**
 * Get human-readable label for an action subtype
 * Derives the name from the ACTION_SUBTYPES config
 * If integrationType and variantId are provided, includes variant name (e.g., "Task - Resident Request")
 */
export function getActionSubtypeLabel(subtype, integrationType, variantId) {
    // Find the base label from action subtype config
    let baseLabel = null;
    for (const integration of ALL_INTEGRATIONS) {
        const actionSubtypes = integration?.ACTION_SUBTYPES;
        if (!actionSubtypes)
            continue;
        const config = actionSubtypes.find(st => st.type === subtype);
        if (config) {
            baseLabel = config.name;
            break;
        }
    }
    // Fallback to the enum value if not found
    if (!baseLabel) {
        return subtype;
    }
    // If variant is provided, look up the variant name
    if (integrationType && variantId) {
        const variants = getActionSubtypeVariants(integrationType, subtype);
        const variant = variants?.find(v => v.id === variantId);
        if (variant) {
            return `${baseLabel} - ${variant.name}`;
        }
    }
    return baseLabel;
}
//# sourceMappingURL=integration-utils.js.map